import { FC, useState } from 'react'

import { Operator } from '@img-arena/consoles-api-streaming'
import { Chip, Stack } from '@mui/material'
import { streamingRestApi } from '@/service/streamingApi'

import { Autocomplete } from '../Autocomplete'
import { extendFiltersFreeTextData, PaginatedResultType } from '../Autocomplete/utils'

export const OPERATOR_AUTOCOMPLETE_MULTISELECT_TEST_IDS = {
  autocomplete: 'operator-autocomplete-multiselect',
  valueChip: (operator: string) => `operator-autocomplete-multiselect-option-${operator}`
} as const

export const OPERATOR_AUTOCOMPLETE_MULTISELECT_LABEL = 'Operators' as const

type Props = {
  value: Operator[]
  onChange: (value: Operator[]) => void
  label?: string
  testId?: string
  getOptionId?: (option: Operator) => string
  withLogos?: boolean
}

export const OperatorAutocompleteMultiselect: FC<Props> = ({ withLogos, ...props }) => {
  const operatorsApi = streamingRestApi.endpoints.operators

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<PaginatedResultType<Operator>>({
    data: [],
    pagination: {
      page: 0,
      pageSize: 100,
      total: 0
    }
  })

  const operators = data.data

  const handleLoadMoreOptions = (page?: number, query?: string) => {
    setLoading(true)

    try {
      operatorsApi
        .getAll({
          page,
          pageSize: data.pagination.pageSize,
          query: query ? query : undefined
        })
        .then((response) => {
          setData((prev) =>
            extendFiltersFreeTextData(prev, {
              data: response.data.content,
              pagination: {
                total: response.data.totalElements,
                page: response.data.number,
                pageSize: response.data.size
              }
            })
          )
        })
    } catch (err) {
      console.error('Error fetching operators:', err)
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Autocomplete
      label={OPERATOR_AUTOCOMPLETE_MULTISELECT_LABEL}
      testId={OPERATOR_AUTOCOMPLETE_MULTISELECT_TEST_IDS.autocomplete}
      {...props}
      loading={loading}
      loadMoreOptions={handleLoadMoreOptions}
      pagination={data.pagination}
      multiselect
      options={operators.sort((a, b) => a.name.localeCompare(b.name))}
      filterOptions={(options, { inputValue }) =>
        options.filter((option) => option.name.toLowerCase().includes(inputValue.toLowerCase()))
      }
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      renderOption={(props, option) => (
        <Stack direction="row" alignItems="center" gap={1} {...props} component="li">
          {withLogos && (
            <img
              src={option.logoUrl || undefined}
              alt={option.name}
              style={{
                width: '1rem',
                height: '1rem',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          )}
          {option.name}
        </Stack>
      )}
      renderValue={(selected) => (
        <>
          {selected.map((operator) => (
            <Chip
              key={operator.id}
              data-testid={OPERATOR_AUTOCOMPLETE_MULTISELECT_TEST_IDS.valueChip(
                String(operator.id)
              )}
              size="small"
              sx={{
                my: 0.4,
                mx: 0.2
              }}
              onDelete={() => props.onChange(selected.filter((o) => o.id !== operator.id))}
              label={
                <Stack key={operator.id} direction="row" alignItems="center" gap={1}>
                  {withLogos && (
                    <img
                      src={operator.logoUrl || ''}
                      alt={operator.name}
                      style={{
                        width: '1rem',
                        height: '1rem',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                  {operator.name}
                </Stack>
              }
            />
          ))}
        </>
      )}
    />
  )
}
